/**
 * In-memory retention of the location a signed-out visitor intended to open.
 *
 * The requested route state lives only in this module-level memory — never in
 * localStorage, sessionStorage, IndexedDB, or any durable browser store — and
 * is restored after an authorized boot exclusively through the existing
 * backend-validated route-state path (URL search -> scope -> boot request),
 * with a deterministic repair to the default destination when the backend
 * rejects the restored route state.
 */

export interface IntendedStudioLocation {
  pathname: string;
  search: Record<string, unknown>;
}

export const DEFAULT_STUDIO_DESTINATION = "/";

let intended: IntendedStudioLocation | null = null;
let repairArmed = false;

/** Records the first intended location of a signed-out episode. */
export function captureIntendedStudioLocation(location: IntendedStudioLocation): void {
  if (intended === null) {
    intended = { pathname: location.pathname, search: { ...location.search } };
  }
}

/** Returns and clears the retained location, arming the one-shot repair. */
export function takeIntendedStudioLocation(): IntendedStudioLocation | null {
  const location = intended;
  intended = null;
  if (location !== null) {
    repairArmed = true;
  }
  return location;
}

/**
 * One-shot deterministic repair: after a restored deep link, a backend
 * rejection (blocked classification) falls back to the default destination
 * exactly once; an accepted restoration disarms the repair.
 */
export function consumeDeepLinkRepair(outcome: "accepted" | "rejected"): boolean {
  if (!repairArmed) {
    return false;
  }
  repairArmed = false;
  return outcome === "rejected";
}

export function resetIntendedStudioLocation(): void {
  intended = null;
  repairArmed = false;
}
